import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useStore } from '../store';
import { Attachment, Guardrail, GuardrailResult, Question, Status, Step, lozFor } from '../types';
import { SpecRenderer } from './SpecRenderer';
import { AttachmentBar } from './AttachmentBar';
import { HistoryModal } from './HistoryModal';
import { GuardrailsEditor } from './GuardrailsEditor';
import { Station, StatusDot, StatusLoz, I } from './Shared';
import { isAdminRole } from '../lib/auth';
import {
  getStepConfluenceProfile,
  setStepConfluenceProfile,
  syncStepToConfluence,
  type StepConfluenceProfile,
  getStepRepoProfile,
  setStepRepoProfile,
  syncStepToRepo,
  type StepRepoProfile,
  setStepGuardrails,
} from '../lib/api';

/**
 * Middle pane — renders the active phase as a list of step cards.
 */
export function StepDetail() {
  const project = useStore((s) => s.project);
  const idx = useStore((s) => s.activePhaseIdx);
  const rerun = useStore((s) => s.rerunStep);
  const projectRole = useStore((s) => s.projectRole);
  const [historyKey, setHistoryKey] = useState<string | null>(null);

  // Status of every step across all phases — used to gate steps whose
  // upstream dependencies haven't completed yet.
  const statusByKey = useMemo(() => {
    const m: Record<string, Status> = {};
    project?.phases.forEach((p) => p.steps.forEach((s) => { m[s.key] = s.status; }));
    return m;
  }, [project]);

  if (!project) return <div style={{ flex: 1 }} />;
  const phase = project.phases[idx];
  if (!phase) return <div style={{ flex: 1 }} />;

  const isViewer = projectRole === 'viewer';
  const nextPending = phase.steps.find((s) => s.status === 'pending');
  const nextBlocked = !!nextPending && unmetDeps(nextPending, statusByKey).length > 0;
  const doneInPhase = phase.steps.filter((s) => s.status === 'done').length;
  const nextDisabledReason = isViewer
    ? 'You have read-only access to this app'
    : nextBlocked
      ? 'An earlier step this one depends on hasn’t run yet'
      : undefined;

  return (
    <section className="scroll" style={{ padding: '22px 28px', minHeight: 0 }}>
      {/* phase header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 6 }}>
        <Station n={idx + 1} size="lg" />
        <div style={{ flex: 1 }}>
          <div className="t-micro" style={{ marginBottom: 2 }}>PHASE {idx + 1} / {project.phases.length}</div>
          <h2 className="t-h1" style={{ margin: 0 }}>{phase.label}</h2>
        </div>
        <GatedAction reason={nextDisabledReason}>
          <button
            className="btn btn--accent btn--sm btn--lg"
            onClick={() => nextPending && rerun(nextPending.key)}
            disabled={!nextPending || isViewer || nextBlocked}
            style={{ height: 34 }}
          >
            {I.play} {nextPending ? `Run next step` : 'All steps done'}
          </button>
        </GatedAction>
      </div>
      <p className="muted" style={{ margin: '4px 0 22px', maxWidth: 560 }}>{phase.desc}</p>

      {/* belt strip */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, padding: '10px 14px', background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 8 }}>
        <span className="t-micro">BELT</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flex: 1 }}>
          {phase.steps.map((s, i) => (
            <React.Fragment key={s.key}>
              <div
                title={s.label}
                style={{
                  flex: 1, height: 8, borderRadius: 2,
                  background:
                    s.status === 'done' ? 'var(--ok)' :
                    s.status === 'in-progress' ? 'var(--accent)' :
                    s.status === 'failed' ? 'var(--err)' :
                    'var(--surface-3)',
                  border: '1px solid var(--line)',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                {s.status === 'in-progress' && (
                  <div className="run-bar" style={{ position: 'absolute', inset: 0, borderRadius: 2, background: 'var(--accent-2)', animation: 'run-slide 1.6s infinite', width: '30%' }} />
                )}
              </div>
              {i < phase.steps.length - 1 && <span style={{ color: 'var(--ink-faint)' }}>&rsaquo;</span>}
            </React.Fragment>
          ))}
        </div>
        <span className="t-micro muted t-num">{doneInPhase}/{phase.steps.length}</span>
      </div>

      {phase.steps.map((step) => (
        <StepCard
          key={step.key}
          step={step}
          isViewer={isViewer}
          statusByKey={statusByKey}
          onShowHistory={() => setHistoryKey(step.key)}
        />
      ))}
      {historyKey && <HistoryModal stepKey={historyKey} onClose={() => setHistoryKey(null)} />}
    </section>
  );
}

function StepCard({ step, isViewer, statusByKey, onShowHistory }: {
  step: Step;
  isViewer: boolean;
  statusByKey: Record<string, Status>;
  onShowHistory: () => void;
}) {
  const showArtifact = useStore((s) => s.showArtifact);
  const rerun = useStore((s) => s.rerunStep);
  const replaceArtifact = useStore((s) => s.replaceArtifact);
  const projectId = useStore((s) => s.project?.id);
  const projectRole = useStore((s) => s.projectRole);
  const currentUser = useStore((s) => s.currentUser);
  const canEditTarget = projectRole === 'owner' || (!!currentUser && isAdminRole(currentUser.role));
  const rpStepKey = useStore((s) => s.rpStepKey);
  const rpOpen = useStore((s) => s.rpOpen);
  const pill = useStore((s) => s.tokenPills[step.key]);
  const liveError = useStore((s) => s.stepErrors[step.key]);
  const retryInfo = useStore((s) => s.stepRetries[step.key]);
  const progressLine = useStore((s) => s.stepProgress[step.key]);
  const stopStep = useStore((s) => s.stopStep);
  const skip = useStore((s) => s.skipStep);
  const startedAt = useStore((s) => s.stepTimers[step.key]);
  const demoTimeScale = useStore((s) => s.demoTimeScale);
  const demoSimRunning = useStore((s) => s.demoSimRunning);
  const hasOverlay = useStore((s) => Boolean(s.activeStepVersions[step.key]));
  const unresolvedComments = useStore((s) =>
    (s.stepComments[step.key] ?? []).filter((c) => !c.resolved),
  );
  const failure = liveError
    ?? (step.lastError ? { message: step.lastError.message, kind: step.lastError.kind } : undefined);
  const status = step.status;

  // Live elapsed timer for in-progress steps (scaled in demo mode)
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (status !== 'in-progress' || !startedAt) { setElapsed(0); return; }
    const scale = demoSimRunning ? demoTimeScale : 1;
    const tick = () => setElapsed(Math.floor(((Date.now() - startedAt) * scale) / 1000));
    tick();
    const iv = setInterval(tick, demoSimRunning ? 100 : 1000);
    return () => clearInterval(iv);
  }, [status, startedAt, demoSimRunning, demoTimeScale]);
  const hasArtifacts = step.artifacts.length > 0;
  const hasPendingEdit = !!step.pendingEdit;
  const isOutdated = !!step.outdatedReason;
  const isPinned = step.pinnedAtVersion != null;
  const isRun = status === 'in-progress';
  const isDone = status === 'done';
  const isFailed = status === 'failed';
  const isAwaiting = status === 'awaiting-input';
  const isSkipped = status === 'skipped';
  const isOpen = rpOpen && rpStepKey === step.key;

  // Skip is offered on steps that haven't produced output and aren't running:
  // a not-yet-run step, a failed one, or one parked awaiting input. Marks the
  // step `skipped` so downstream steps can proceed without it.
  const canSkip = !isViewer && (status === 'pending' || isFailed || isAwaiting);
  const handleSkip = (): void => {
    if (window.confirm('Skip this step? It will be marked skipped and downstream steps will run without its output.')) {
      skip(step.key);
    }
  };

  const rerunLabel = step.rerunLabel ?? 'Re-run';
  const runLabel = step.runLabel ?? 'Run';

  // Gate the run/re-run/retry controls: viewers can't mutate the project,
  // and a step can't run until every step it depends on has completed.
  const blockingDeps = unmetDeps(step, statusByKey);
  const blockedByDeps = blockingDeps.length > 0;
  const runDisabled = isViewer || blockedByDeps;
  const disabledReason = isViewer
    ? 'You have read-only access to this app'
    : blockedByDeps
      ? `Run ${blockingDeps.join(', ')} first — this step depends on it`
      : undefined;

  // Manually import a file as an artifact instead of running the step. Available
  // on real, editable projects — lets a not-yet-run step be seeded from a file.
  const canImport = !isViewer && !!projectId?.startsWith('p_');
  const importRef = useRef<HTMLInputElement>(null);
  const [importing, setImporting] = useState(false);
  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImporting(true);
    try {
      await replaceArtifact(step.key, file.name, await file.text());
    } catch (err: any) {
      window.alert(`Could not import file: ${err?.message ?? 'unknown error'}`);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div
      className="card"
      data-step-key={step.key}
      style={{
        padding: 0,
        marginBottom: 10,
        borderLeft: isPinned ? '3px solid var(--warn)' : '1px solid var(--line)',
        background: 'var(--surface)',
        boxShadow: isOpen ? '0 0 0 1px var(--ink)' : 'var(--shadow-1)',
        transition: 'all .15s',
      }}
    >
      <div style={{ padding: '16px 18px', display: 'flex', alignItems: 'flex-start', gap: 14 }}>
        <div style={{ paddingTop: 2 }}>
          <StatusDot status={status} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <span className="t-h3">{step.label}</span>
            <span className="kbd">{step.key}</span>
            {step.version > 0 && <span className="t-micro muted">v{step.version}</span>}
            {isPinned && (
              <span className="loz loz--stale" style={{ height: 18, padding: '0 6px' }}>
                {I.pin} pinned
              </span>
            )}
            <span style={{ flex: 1 }} />
            <StatusLoz status={(isOutdated || hasPendingEdit) && isDone ? 'stale' : status}>
              {(isOutdated || hasPendingEdit) && isDone ? 'Out of date' : undefined}
            </StatusLoz>
          </div>
          <div className="t-small muted" style={{ marginBottom: isDone || isRun ? 12 : 0 }}>
            {step.skillRef && (
              <>
                <span className="mono" style={{ color: 'var(--ink-2)' }}>skill/</span>
                <span className="mono" style={{ color: 'var(--ink-2)' }}>{step.skillRef}</span>
                <span> · </span>
              </>
            )}
            {step.desc}
          </div>

          {step.extras && (
            <div style={{ marginBottom: 8 }}>
              <SpecRenderer node={step.extras} />
            </div>
          )}

          {isOutdated && isDone && (
            <div
              style={{
                marginBottom: 8,
                padding: '10px 12px',
                border: '1px solid var(--warn)',
                borderLeft: '3px solid var(--warn)',
                borderRadius: 4,
                background: 'color-mix(in srgb, var(--warn) 8%, var(--surface))',
                fontSize: 12.5,
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: 4, color: 'var(--warn)' }}>
                Outdated — please re-run
              </div>
              <div className="muted">{step.outdatedReason}</div>
            </div>
          )}

          {hasPendingEdit && !isOutdated && isDone && (
            <div style={{ marginBottom: 8, padding: '8px 10px', background: 'var(--surface-2)', borderRadius: 4, fontSize: 12.5 }}>
              <span>✎ </span>
              <span>Edit pending -- click {rerunLabel} to apply: "{trim(step.pendingEdit!, 80)}"</span>
            </div>
          )}

          {/* progress for in-progress */}
          {isRun && (
            <div style={{ marginTop: 10, marginBottom: 12 }}>
              <div
                style={{
                  height: 4,
                  borderRadius: 2,
                  background: 'var(--surface-3)',
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                <div className="run-bar" style={{
                  position: 'absolute', inset: 0, width: '40%',
                  background: 'var(--run)',
                  borderRadius: 2,
                  animation: 'run-slide 1.6s ease-in-out infinite',
                }} />
              </div>
              <div className="t-small muted" style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="mono" style={{ color: 'var(--accent-2)' }}>{formatDuration(elapsed * 1000)}</span>
                <span>·</span>
                <span>Executing <span className="mono">{step.skillRef || step.key}</span> skill...</span>
              </div>

              {/* Live agentic/hybrid activity (tool calls + the `↳ ended:` line). */}
              {progressLine && (
                <div
                  className="t-small muted mono"
                  style={{
                    marginTop: 6,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    color: 'var(--accent-2)',
                  }}
                  title={progressLine}
                >
                  {progressLine}
                </div>
              )}

              {retryInfo && (
                <div
                  style={{
                    marginTop: 10,
                    padding: '8px 10px',
                    border: '1px solid var(--warn)',
                    borderLeft: '3px solid var(--warn)',
                    borderRadius: 4,
                    background: 'color-mix(in srgb, var(--warn) 8%, var(--surface))',
                    fontSize: 12.5,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontWeight: 600, color: 'var(--warn)' }}>
                      {retryInfo.kind === 'rate-limit' ? 'Rate-limited' : 'Invalid response'}
                      {' — attempt failed, retrying '}
                      {retryInfo.attempt}/{retryInfo.max}
                      {retryInfo.nextDelayMs ? ` in ${Math.round(retryInfo.nextDelayMs / 1000)}s` : ''}
                    </span>
                    <span style={{ flex: 1 }} />
                    {!isViewer && (
                      <button
                        className="btn btn--ghost btn--sm"
                        onClick={() => stopStep(step.key)}
                        title="Stop retrying this step"
                      >
                        Stop
                      </button>
                    )}
                  </div>
                  <div className="muted mono" style={{ marginTop: 4, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {trim(retryInfo.reason, 240)}
                  </div>
                </div>
              )}
            </div>
          )}

          {isAwaiting && step.pendingQuestions && step.pendingQuestions.length > 0 && (
            <QuestionPanel stepKey={step.key} questions={step.pendingQuestions} isViewer={isViewer} />
          )}

          {isFailed && (
            <div
              style={{
                marginTop: 8,
                marginBottom: 4,
                padding: '10px 12px',
                border: '1px solid var(--err)',
                borderLeft: '3px solid var(--err)',
                borderRadius: 4,
                background: 'color-mix(in srgb, var(--err) 8%, var(--surface))',
              }}
            >
              <div
                className="t-small"
                style={{
                  color: 'var(--err)',
                  fontWeight: 600,
                  marginBottom: failure?.message ? 6 : 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <span>
                  {failure?.kind === 'budget'
                    ? 'Budget exceeded'
                    : failure?.kind === 'stopped'
                      ? 'Stopped by you'
                      : 'Step failed'}
                </span>
                <span className="t-micro muted" style={{ fontWeight: 400 }}>
                  · click Retry to re-run
                </span>
              </div>
              {failure?.message && (
                <pre
                  className="mono"
                  style={{
                    margin: 0,
                    fontSize: 12,
                    color: 'var(--ink-2)',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    lineHeight: 1.45,
                  }}
                >
                  {failure.message}
                </pre>
              )}
            </div>
          )}

          {/* actions */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: isDone || isRun || isFailed || isAwaiting ? 0 : 12 }}>
            {status === 'pending' && (
              <GatedAction reason={disabledReason}>
                <button
                  className="btn btn--sm"
                  onClick={() => rerun(step.key)}
                  disabled={runDisabled}
                >
                  {I.play} {runLabel}
                </button>
              </GatedAction>
            )}
            {(status === 'pending' || isFailed) && canImport && (
              <>
                <button
                  className="btn btn--ghost btn--sm"
                  disabled={importing}
                  onClick={() => importRef.current?.click()}
                  title="Import a file as this step's artifact without running it"
                >
                  {I.upload} {importing ? 'Importing…' : 'Import file'}
                </button>
                <input
                  ref={importRef}
                  type="file"
                  style={{ display: 'none' }}
                  onChange={(e) => void handleImportFile(e)}
                />
              </>
            )}
            {isDone && (
              <>
                {hasArtifacts && (
                  <button className={`btn btn--sm ${isOpen ? 'btn--primary' : ''}`} onClick={() => showArtifact(step.key)}>
                    {step.artifacts.length} artifact{step.artifacts.length !== 1 ? 's' : ''} · View
                  </button>
                )}
                <GatedAction reason={disabledReason}>
                  {unresolvedComments.length > 0 && (
                  <button
                    className="btn btn--sm"
                    onClick={() => showArtifact(step.key, 'reviews')}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                  >
                    <span
                      style={{
                        background: 'var(--accent)',
                        color: '#fff',
                        borderRadius: 10,
                        padding: '0 6px',
                        fontSize: 10,
                        lineHeight: '16px',
                        minWidth: 16,
                        textAlign: 'center',
                      }}
                    >
                      {unresolvedComments.length}
                    </span>
                    review{unresolvedComments.length !== 1 ? 's' : ''}
                  </button>
                )}
                <button
                    className={`btn btn--sm ${isOutdated ? 'btn--accent' : 'btn--ghost'}`}
                    onClick={() => rerun(step.key)}
                    disabled={runDisabled}
                  >
                    {I.refresh} {hasPendingEdit ? rerunLabel : 'Re-run'}
                  </button>
                </GatedAction>
              </>
            )}
            {isFailed && (
              <>
                {hasArtifacts && (
                  <button className="btn btn--sm" onClick={() => showArtifact(step.key)}>
                    {step.artifacts.length} artifact{step.artifacts.length !== 1 ? 's' : ''} · View
                  </button>
                )}
                <GatedAction reason={disabledReason}>
                  <button
                    className="btn btn--ghost btn--sm"
                    onClick={() => rerun(step.key)}
                    disabled={runDisabled}
                  >
                    {I.refresh} Retry
                  </button>
                </GatedAction>
              </>
            )}
            {isSkipped && (
              <GatedAction reason={disabledReason}>
                <button
                  className="btn btn--sm"
                  onClick={() => rerun(step.key)}
                  disabled={runDisabled}
                  title="Run this step after all — undoes the skip"
                >
                  {I.play} {runLabel}
                </button>
              </GatedAction>
            )}
            {isRun && (
              <button className="btn btn--sm" disabled>
                Running...
              </button>
            )}
            {canSkip && (
              <button
                className="btn btn--ghost btn--sm"
                onClick={handleSkip}
                title="Skip this step — mark it skipped so downstream steps can run without its output"
              >
                Skip
              </button>
            )}
            {(hasOverlay || (step.version >= 1 && (isDone || isFailed))) && (
              <button className="btn btn--ghost btn--sm" onClick={onShowHistory}>
                History
              </button>
            )}
            <span style={{ flex: 1 }} />
            {isDone && projectId && (
              <StepConfluenceTarget projectId={projectId} stepKey={step.key} canEdit={canEditTarget} />
            )}
            {isDone && projectId && (
              <StepRepoTarget projectId={projectId} stepKey={step.key} canEdit={canEditTarget} />
            )}
            {step.lastRunDurationMs != null && (isDone || isFailed) && (
              <span className="t-micro muted" title={step.lastRunAt ? `Last run: ${new Date(step.lastRunAt).toLocaleString()}` : ''}>
                {formatDuration(step.lastRunDurationMs)}
                {(step.totalTimeMs ?? 0) > step.lastRunDurationMs * 1.1 && (
                  <span style={{ opacity: 0.6 }}> (total: {formatDuration(step.totalTimeMs!)})</span>
                )}
              </span>
            )}
            {/* Cost: prefer the live SSE pill; after a reload it's gone, so fall
                back to the value persisted on the step. A leading "≈" marks a
                token-table estimate (single-shot); exact costs (agentic Agent
                SDK) show no marker. */}
            {pill ? (
              <span
                className="t-micro muted"
                title={pill.estimated ? 'Estimated from token counts (single-shot)' : 'Exact cost reported by the Agent SDK'}
              >
                {pill.estimated ? '≈' : ''}${pill.costUsd.toFixed(3)}
              </span>
            ) : step.lastRunCostUsd != null && (isDone || isFailed) ? (
              <span
                className="t-micro muted"
                title={step.lastRunCostEstimated ? 'Estimated from token counts (single-shot)' : 'Exact cost reported by the Agent SDK'}
              >
                {step.lastRunCostEstimated ? '≈' : ''}${step.lastRunCostUsd.toFixed(3)}
              </span>
            ) : null}
          </div>

          <GuardrailsSection
            step={step}
            projectId={projectId}
            canEdit={!isViewer}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * Per-step guardrails: shows the outcome of the last evaluation (pass / warn /
 * fail) and lets an editor tune the app-level guardrail set. The set is seeded
 * from the assembly line at project creation, then edited here per app.
 */
function GuardrailsSection({
  step,
  projectId,
  canEdit,
}: {
  step: Step;
  projectId: string | undefined;
  canEdit: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Guardrail[]>(step.guardrails ?? []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Keep the draft in sync when the step's guardrails change out from under us
  // (reload, version switch) — but only while the editor is closed so we don't
  // clobber in-progress edits.
  useEffect(() => {
    if (!open) setDraft(step.guardrails ?? []);
  }, [step.guardrails, open]);

  const results = step.guardrailResults ?? [];
  const configured = step.guardrails ?? [];
  const dirty = JSON.stringify(draft) !== JSON.stringify(configured);

  const save = async (): Promise<void> => {
    if (!projectId) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await setStepGuardrails(projectId, step.key, draft);
      useStore.setState({ project: updated });
      setOpen(false);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to save guardrails');
    } finally {
      setSaving(false);
    }
  };

  // Nothing to show and nothing editable → render nothing (keeps clean steps clean).
  if (results.length === 0 && configured.length === 0 && !canEdit) return null;

  return (
    <div style={{ marginTop: 12, borderTop: '1px solid var(--line)', paddingTop: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span className="t-micro muted" style={{ letterSpacing: '0.05em' }}>GUARDRAILS</span>
        {configured.length > 0 && (
          <span className="t-micro muted">· {configured.length} configured</span>
        )}
        <span style={{ flex: 1 }} />
        {canEdit && (
          <button className="btn btn--ghost btn--sm" onClick={() => setOpen((o) => !o)}>
            {open ? 'Close' : configured.length > 0 ? 'Edit' : 'Add guardrails'}
          </button>
        )}
      </div>

      {results.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
          {results.map((r) => (
            <GuardrailResultRow key={`${r.id}-${r.checkedAt ?? ''}`} result={r} />
          ))}
        </div>
      )}

      {open && (
        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <GuardrailsEditor
            guardrails={draft}
            onChange={setDraft}
            onSave={() => void save()}
            saving={saving}
            canSave={dirty}
            saveLabel="Save guardrails"
          />
          {error && <span className="t-small" style={{ color: 'var(--err)' }}>{error}</span>}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button className="btn btn--ghost btn--sm" onClick={() => { setDraft(configured); setOpen(false); }}>
              Cancel
            </button>
            <span className="t-micro muted">Applies on this step’s next run.</span>
          </div>
        </div>
      )}
    </div>
  );
}

function GuardrailResultRow({ result }: { result: GuardrailResult }) {
  const color = result.pass
    ? 'var(--ok, #22a06b)'
    : result.enforcement === 'warn'
      ? 'var(--warn, #b38600)'
      : 'var(--err)';
  const tag = result.pass ? 'pass' : result.enforcement === 'warn' ? 'warn' : 'fail';
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', fontSize: 12 }}>
      <span
        className="mono"
        style={{ color, fontWeight: 600, minWidth: 34, textTransform: 'uppercase' }}
      >
        {tag}
      </span>
      <span style={{ minWidth: 0, wordBreak: 'break-word' }}>
        <span>{result.text}</span>
        {result.message && <span className="muted"> — {result.message}</span>}
      </span>
    </div>
  );
}

/**
 * Compact per-step Confluence control: pick the connection this step syncs to
 * ("Inherit" follows the app's target, then the workspace default; an explicit
 * pick overrides just this step), and a "Sync step" button that publishes ONLY
 * this step's artifacts to that connection — without touching the rest of the
 * app. Hidden when the workspace has no connections. Target is read-only for
 * non-owners; anyone with access can trigger the sync.
 */
function StepConfluenceTarget({
  projectId, stepKey, canEdit,
}: { projectId: string; stepKey: string; canEdit: boolean }) {
  const [data, setData] = useState<StepConfluenceProfile | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    let alive = true;
    getStepConfluenceProfile(projectId, stepKey)
      .then((d) => { if (alive) setData(d); })
      .catch(() => { if (alive) setData(null); });
    return () => { alive = false; };
  }, [projectId, stepKey]);

  // Hidden when the assemblyLine disabled in-step sync for this step, or the
  // workspace has no connections to sync to.
  if (!data || !data.stepSyncEnabled || data.profiles.length === 0) return null;

  // The connection used when this step inherits: the app selection, else default.
  const inherited =
    data.profiles.find((p) => p.id === data.projectProfileId) ??
    data.profiles.find((p) => p.isDefault);
  const inheritLabel = `Inherit${inherited ? ` (${inherited.name})` : ''}`;
  const effective = data.profiles.find((p) => p.id === data.effectiveProfileId);

  const onChange = async (next: string): Promise<void> => {
    setBusy(true);
    try {
      const r = await setStepConfluenceProfile(projectId, stepKey, next || null);
      setData((d) => (d ? { ...d, stepProfileId: r.stepProfileId, effectiveProfileId: r.effectiveProfileId } : d));
      // Re-targeting flips the step's review panel + Confluence links to the
      // new connection (the version stays put — this is a routing change, not a
      // view switch). See PLAN §4.6 / §5.3.
      void useStore.getState().fetchComments(stepKey);
      void useStore.getState().fetchPublishedDocs(stepKey);
    } catch {
      // keep current; reloads reflect server state
    } finally {
      setBusy(false);
    }
  };

  const syncStep = async (): Promise<void> => {
    setSyncing(true); setMsg(null);
    try {
      const r = await syncStepToConfluence(projectId, stepKey);
      // Merge the fresh page refs so the artifact panel shows Confluence links.
      useStore.setState((s) => ({ publishedDocs: { ...s.publishedDocs, ...r.publishedDocs } }));
      // Pages now exist on the target connection — pull its comments in.
      void useStore.getState().fetchComments(stepKey);
      setMsg({ ok: true, text: r.published > 0 ? `Synced ${r.published}` : 'Nothing to sync' });
      // Success auto-dismisses; errors stay put so the user can read them.
      setTimeout(() => setMsg(null), 4000);
    } catch (e) {
      // Surface the exact publish failure (e.g. a Confluence title conflict) and
      // leave it on screen until the next sync attempt.
      setMsg({ ok: false, text: e instanceof Error ? e.message : 'Sync failed' });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <span style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        className="btn btn--ghost btn--sm"
        onClick={() => setOpen((o) => !o)}
        title={effective ? `Confluence sync: ${effective.name}` : 'Confluence sync'}
        aria-label="Confluence sync"
      >
        {I.confluence}
      </button>
      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={() => setOpen(false)} />
          <div
            style={{
              position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 41,
              width: 300, maxWidth: '90vw', padding: 12, borderRadius: 10,
              background: 'var(--surface, #fff)', border: '1px solid var(--line, #e2e2e2)',
              boxShadow: '0 8px 28px rgba(0,0,0,0.18)', display: 'flex', flexDirection: 'column', gap: 10,
            }}
          >
            <div className="t-micro" style={{ fontWeight: 600 }}>Confluence sync · this step</div>

            {canEdit ? (
              <label className="t-micro muted" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                Connection
                <select
                  className="select select--sm"
                  value={data.stepProfileId ?? ''}
                  disabled={busy || syncing}
                  onChange={(e) => void onChange(e.target.value)}
                >
                  <option value="">{inheritLabel}</option>
                  {data.profiles.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}{p.isDefault ? ' ★' : ''}</option>
                  ))}
                </select>
              </label>
            ) : (
              <div className="t-micro muted">
                Connection: {effective ? effective.name : '—'}{data.stepProfileId ? '' : ' (inherited)'}
              </div>
            )}

            <button
              className="btn btn--sm btn--primary"
              disabled={syncing || !effective}
              onClick={() => void syncStep()}
              title={effective ? `Publish only this step to ${effective.name}` : 'No usable connection'}
            >
              {syncing ? 'Syncing…' : <>{I.confluence} Sync step</>}
            </button>

            {msg && (
              <div
                className="t-micro"
                style={{
                  color: msg.ok ? 'var(--ok, #128a4b)' : 'var(--err, #c0362c)',
                  maxHeight: 120, overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  background: 'var(--surface-2, #f6f6f6)', borderRadius: 6, padding: '6px 8px',
                }}
              >
                {msg.ok ? '✓ ' : '✗ '}{msg.text}
              </div>
            )}
          </div>
        </>
      )}
    </span>
  );
}

/**
 * Compact per-step git control, mirroring StepConfluenceTarget: pick the git
 * connection this step pushes to ("Inherit" follows the app's target, then the
 * workspace default), and a "Push step" button that commits ONLY this step's
 * artifacts to that connection. Hidden when the workspace has no git
 * connections or the step's in-step sync is disabled.
 */
function StepRepoTarget({
  projectId, stepKey, canEdit,
}: { projectId: string; stepKey: string; canEdit: boolean }) {
  const [data, setData] = useState<StepRepoProfile | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    let alive = true;
    getStepRepoProfile(projectId, stepKey)
      .then((d) => { if (alive) setData(d); })
      .catch(() => { if (alive) setData(null); });
    return () => { alive = false; };
  }, [projectId, stepKey]);

  if (!data || !data.stepSyncEnabled || data.profiles.length === 0) return null;

  const inherited =
    data.profiles.find((p) => p.id === data.projectProfileId) ??
    data.profiles.find((p) => p.isDefault);
  const inheritLabel = `Inherit${inherited ? ` (${inherited.name})` : ''}`;
  const effective = data.profiles.find((p) => p.id === data.effectiveProfileId);

  const onChange = async (next: string): Promise<void> => {
    setBusy(true);
    try {
      const r = await setStepRepoProfile(projectId, stepKey, next || null);
      setData((d) => (d ? { ...d, stepProfileId: r.stepProfileId, effectiveProfileId: r.effectiveProfileId } : d));
    } catch {
      // keep current; reloads reflect server state
    } finally {
      setBusy(false);
    }
  };

  const pushStep = async (): Promise<void> => {
    setSyncing(true); setMsg(null);
    try {
      const r = await syncStepToRepo(projectId, stepKey);
      setMsg({ ok: true, text: r.changed > 0 ? `Pushed ${r.changed}` : 'Up to date' });
      setTimeout(() => setMsg(null), 4000);
    } catch (e) {
      setMsg({ ok: false, text: e instanceof Error ? e.message : 'Push failed' });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <span style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        className="btn btn--ghost btn--sm"
        onClick={() => setOpen((o) => !o)}
        title={effective ? `Git push: ${effective.name}` : 'Git push'}
        aria-label="Git push"
      >
        {I.git}
      </button>
      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={() => setOpen(false)} />
          <div
            style={{
              position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 41,
              width: 300, maxWidth: '90vw', padding: 12, borderRadius: 10,
              background: 'var(--surface, #fff)', border: '1px solid var(--line, #e2e2e2)',
              boxShadow: '0 8px 28px rgba(0,0,0,0.18)', display: 'flex', flexDirection: 'column', gap: 10,
            }}
          >
            <div className="t-micro" style={{ fontWeight: 600 }}>Git push · this step</div>

            {canEdit ? (
              <label className="t-micro muted" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                Connection
                <select
                  className="select select--sm"
                  value={data.stepProfileId ?? ''}
                  disabled={busy || syncing}
                  onChange={(e) => void onChange(e.target.value)}
                >
                  <option value="">{inheritLabel}</option>
                  {data.profiles.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}{p.isDefault ? ' ★' : ''}</option>
                  ))}
                </select>
              </label>
            ) : (
              <div className="t-micro muted">
                Connection: {effective ? effective.name : '—'}{data.stepProfileId ? '' : ' (inherited)'}
              </div>
            )}

            <button
              className="btn btn--sm btn--primary"
              disabled={syncing || !effective}
              onClick={() => void pushStep()}
              title={effective ? `Push only this step to ${effective.name}` : 'No usable connection'}
            >
              {syncing ? 'Pushing…' : <>{I.git} Push step</>}
            </button>

            {msg && (
              <div
                className="t-micro"
                style={{
                  color: msg.ok ? 'var(--ok, #128a4b)' : 'var(--err, #c0362c)',
                  maxHeight: 120, overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  background: 'var(--surface-2, #f6f6f6)', borderRadius: 6, padding: '6px 8px',
                }}
              >
                {msg.ok ? '✓ ' : '✗ '}{msg.text}
              </div>
            )}
          </div>
        </>
      )}
    </span>
  );
}

/**
 * Returns the keys of this step's upstream dependencies that have not yet been
 * satisfied. A dependency counts as satisfied when it is `done` or `skipped`
 * (a skipped upstream produces no artifacts but no longer blocks its
 * dependents). An empty array means the step is runnable.
 */
function unmetDeps(step: Step, statusByKey: Record<string, Status>): string[] {
  return (step.dependsOn ?? []).filter(
    (key) => statusByKey[key] !== 'done' && statusByKey[key] !== 'skipped',
  );
}

/**
 * Wraps a (possibly disabled) button so its tooltip still shows. Disabled
 * buttons receive no pointer events, so a native `title` on the button itself
 * never fires in most browsers; the wrapping span does receive hover.
 */
function GatedAction({ reason, children }: { reason?: string; children: React.ReactNode }) {
  if (!reason) return <>{children}</>;
  return <span title={reason} style={{ display: 'inline-flex' }}>{children}</span>;
}

function trim(s: string, n: number): string {
  return s.length > n ? s.slice(0, n - 1) + '...' : s;
}

function formatDuration(ms: number): string {
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  if (m < 60) return rem > 0 ? `${m}m ${rem}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const remM = m % 60;
  return remM > 0 ? `${h}h ${remM}m` : `${h}h`;
}

/**
 * Inline form for answering the clarification questions a step raised.
 * Renders a radio list when `question.choices` is present, a textarea
 * otherwise. Submit is enabled once every question has a non-empty answer.
 */
function QuestionPanel({ stepKey, questions, isViewer }: { stepKey: string; questions: Question[]; isViewer: boolean }) {
  const submitAnswers = useStore((s) => s.submitAnswers);
  const projectId = useStore((s) => s.project?.id);
  const [answers, setAnswers] = useState<Record<string, string>>(() => {
    const seed: Record<string, string> = {};
    for (const q of questions) if (q.answer) seed[q.id] = q.answer;
    return seed;
  });
  const [submitting, setSubmitting] = useState(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);

  // Reset form when the LLM raises a new set of questions on a re-run.
  const questionSig = questions.map((q) => q.id).join('|');
  useEffect(() => {
    const seed: Record<string, string> = {};
    for (const q of questions) if (q.answer) seed[q.id] = q.answer;
    setAnswers(seed);
    setAttachments([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionSig]);

  const allAnswered = questions.every((q) => (answers[q.id] ?? '').trim().length > 0);
  const isLocalOnly = !projectId?.startsWith('p_');

  const setAnswer = (id: string, value: string) => {
    setAnswers((prev) => ({ ...prev, [id]: value }));
  };

  const onSubmit = async () => {
    if (!allAnswered || submitting) return;
    setSubmitting(true);
    try {
      await submitAnswers(stepKey, answers, attachments.length ? attachments : undefined);
      setAttachments([]);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        marginTop: 8,
        marginBottom: 4,
        padding: '12px 14px',
        border: '1px solid var(--warn, var(--line))',
        borderLeft: '3px solid var(--warn, var(--accent))',
        borderRadius: 4,
        background: 'var(--surface-2)',
      }}
    >
      <div
        className="t-small"
        style={{ fontWeight: 600, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}
      >
        <span>The skill needs a bit more input</span>
        <span className="t-micro muted" style={{ fontWeight: 400 }}>
          · {questions.length} question{questions.length !== 1 ? 's' : ''}
        </span>
      </div>

      {questions.map((q, idx) => (
        <div key={q.id} style={{ marginBottom: idx === questions.length - 1 ? 12 : 14 }}>
          <div className="t-small" style={{ marginBottom: 6 }}>
            <span className="t-num muted" style={{ marginRight: 6 }}>{idx + 1}.</span>
            {q.prompt}
          </div>
          {q.choices && q.choices.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, paddingLeft: 18 }}>
              {q.choices.map((choice) => {
                const checked = answers[q.id] === choice;
                return (
                  <label
                    key={choice}
                    className="t-small"
                    style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
                  >
                    <input
                      type="radio"
                      name={`q-${stepKey}-${q.id}`}
                      value={choice}
                      checked={checked}
                      onChange={() => setAnswer(q.id, choice)}
                    />
                    <span>{choice}</span>
                  </label>
                );
              })}
            </div>
          ) : q.secret ? (
            <input
              type="password"
              className="textarea"
              placeholder="Your answer (stored encrypted)..."
              value={answers[q.id] ?? ''}
              autoComplete="off"
              onChange={(e) => setAnswer(q.id, e.target.value)}
              style={{
                background: 'var(--surface)',
                marginLeft: 18,
                width: 'calc(100% - 18px)',
                fontFamily: 'monospace',
              }}
            />
          ) : (
            <textarea
              className="textarea"
              rows={2}
              placeholder="Your answer..."
              value={answers[q.id] ?? ''}
              onChange={(e) => setAnswer(q.id, e.target.value)}
              style={{ background: 'var(--surface)', marginLeft: 18, width: 'calc(100% - 18px)' }}
            />
          )}
        </div>
      ))}

      {!isLocalOnly && (
        <div style={{ marginBottom: 10 }}>
          <AttachmentBar attachments={attachments} onChange={setAttachments} disabled={isViewer} compact />
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <GatedAction reason={isViewer ? 'You have read-only access to this app' : undefined}>
          <button
            className="btn btn--accent btn--sm"
            disabled={!allAnswered || submitting || isLocalOnly || isViewer}
            onClick={onSubmit}
          >
            {submitting ? 'Submitting...' : 'Submit answers'}
          </button>
        </GatedAction>
        {isLocalOnly && (
          <span className="t-micro muted">Demo mode — answers aren't sent to the server.</span>
        )}
      </div>
    </div>
  );
}
